import {
    Component,
    Inject,
    OnInit,
} from "@angular/core";
import {
    MatDialog,
    MatDialogRef,
    MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Proposal } from "app/entities/proposal";
import { ProposalService } from "app/main/pages/proposals/proposals.service";
import { AlertService } from "app/services/alert.service";
import { GtagService } from "app/services/gtag.service";
import { AuthService } from "app/services/auth.service";
import { buildProposalShareMessage, buildWhatsappUrl } from "app/helpers/proposal-share";
import { copyTextWhenReady } from "app/helpers/clipboard";
import { openPendingWindow } from "app/helpers/pending-window";
import { trackProposalSend } from "app/helpers/proposal-funnel";
import { ProposalEventService } from "app/services/proposal.service";
import { environment } from "environments/environment";
import { PreviewProposalDialogComponent } from "../preview-proposal-dialog/preview-proposal-dialog.component";
import { SubscriptionRestrictionService } from "app/services/subscription-restriction.service";

@Component({
    selector: "app-proposal-send-dialog",
    templateUrl: "./proposal-send-dialog.component.html",
    styleUrls: ["./proposal-send-dialog.component.scss"],
})
export class ProposalSendDialogComponent implements OnInit {
    proposal!: Proposal;

    env = environment

    constructor(
        public matDialogRef: MatDialogRef<ProposalSendDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public _matDialog: MatDialog,
        private _proposalsService: ProposalService,
        private _alertService: AlertService,
        private _router: Router,
        private _proposalEventService: ProposalEventService, 
        private _gtag: GtagService,
        private _subscriptionRestrictionService: SubscriptionRestrictionService,
        private _authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.proposal = this.data.proposal;
    }

    goHome() {
        this.trackSendEvent('dialog_proposal_go_home', 'Retorno para a dashboard');
        this.matDialogRef.close();
        this._router.navigateByUrl("/dashboard");
    }

    copy(source) {
        const request = this._proposalsService.copyLink(this.proposal.id, source).toPromise();
        // A cópia começa ainda no clique, senão o Safari não copia (ORCA-1925)
        const copied = copyTextWhenReady(request.then((res) => this.getMessageWithLinkProposal(res.data.link_token)));

        request.then(
            (res) => {
                this.trackSendEvent('dialog_proposal_link_copied', 'Link do orçamento copiado', {
                    source
                });
                trackProposalSend(this._gtag, this.proposal, 'link', 'dialog_after_create');

                if (this.proposal.status! <= 2) {
                    this.proposal.status = 2;
                    this.proposal.statusClass = "blue-800";
                    this.proposal.statusName = "Enviado";
                }
                copied.then((ok) => ok
                    ? this._alertService.success("Link copiado")
                    : this._alertService.error("Não foi possível copiar o link."));
            },
            (error) => {
                this._alertService.error(error);
            }
        );
    }

    openPDF() {
        var featureLimited = this._subscriptionRestrictionService.checkFeatureLimitAndShowDialog("pdf-propostas")
        if (!featureLimited) {
            trackProposalSend(this._gtag, this.proposal, 'pdf', 'dialog_after_create');
            const timestamp = new Date().getTime()
            var url = this.env.baseUrlHotsite + '/proposta/' + this.proposal.tokenLink + '.pdf?v=' + timestamp
            window.open(url, '_blank')
        }
    }


    sendWhatsapp(source) {
        // A aba abre ainda no clique, senão o Safari bloqueia (ORCA-1925)
        const whatsappWindow = openPendingWindow();

        this._proposalsService.copyLink(this.proposal.id, source).subscribe(
            (res) => {
                this.trackSendEvent('dialog_proposal_sent_whatsapp', 'Orçamento enviado via WhatsApp', {
                    channel: 'whatsapp',
                    contact_phone: this.proposal!.client?.phone,
                    source
                });
                trackProposalSend(this._gtag, this.proposal, 'whatsapp', 'dialog_after_create');

                let link = res.data.link_token;

                if (this.proposal.status! <= 2) {
                    this.proposal.status = 2;
                    this.proposal.statusClass = "blue-800";
                    this.proposal.statusName = "Enviado";
                }
                const urlWhatsapp = buildWhatsappUrl(this.proposal!.client?.phone, this.getMessageWithLinkProposal(link));
                whatsappWindow.navigate(urlWhatsapp);
            },
            (error) => {
                whatsappWindow.close();
                this._alertService.error(error);
            }
        );
    }

    sendEmail(contact: any = null, contactEmail = null) {
        if (this.proposal?.client?.email == null) {
            return;
        }

        if (contact && !contactEmail) {
            contact = {
                contact_id: contact.id,
            };
        }
        if (contactEmail) {
            contact = {
                contact_email: contactEmail,
            };
        }

        this._proposalsService.sendEmail(this.proposal.id!, contact).subscribe(
            (data) => {
                this.trackSendEvent('dialog_proposal_sent_email', 'Orçamento enviado via e-mail', {
                    channel: 'email',
                    contact_id: contact?.contact_id,
                    contact_email: contact?.contact_email ?? this.proposal?.client?.email,
                });
                trackProposalSend(this._gtag, this.proposal, 'email', 'dialog_after_create');

                this._alertService.success("Email enviado com sucesso.");
                this._proposalEventService.initOnNext();
            },
            (err) => {
                this._alertService.error(err);
            }
        );
    }

    preview() {
        this.trackSendEvent('dialog_proposal_preview', 'Pré-visualização do orçamento');

        let dialog = PreviewProposalDialogComponent.open(this._matDialog, {
            id: this.proposal?.id,
            token: this.proposal?.tokenLink,
        });
        dialog.afterClosed().subscribe((result) => {});
    }

    getMessageWithLinkProposal(link) {
        const account = this._authService.currentAccount;
        return buildProposalShareMessage(this.proposal as any, link, account?.legalName || account?.name);
    }

    private trackSendEvent(eventName: string, eventLabel: string, extra: Record<string, any> = {}): void {
        const payload: Record<string, any> = {
            event_category: 'proposal_delivery',
            event_label: eventLabel,
            proposal_id: this.proposal?.id,
            client_id: this.proposal?.client?.id ?? this.proposal?.clientId,
            proposal_status: this.proposal?.status,
            ...extra,
        };

        Object.keys(payload).forEach((key) => {
            if (payload[key] === undefined || payload[key] === null) {
                delete payload[key];
            }
        });

        this._gtag.event(eventName, payload);
    }
}
